import { createFileRoute, Outlet, redirect } from '@tanstack/react-router';

import {
  getTeamMembersOptions,
  getTeamOptions,
} from 'client/@tanstack/react-query.gen';
import { newRecommendationsSearchSchema } from 'components/NewRecommendations';
import { isPersonAdmin } from 'helpers/query';
import { findExecutiveClientMemberId } from 'helpers/team';
import { editUserPipelineSearchSchema } from 'routes/_private/_route/users/$userId/pipelines/$pipelineId/edit.route';
import { userPipelineDetailsSearchSchema } from 'routes/_private/_route/users/$userId/pipelines/$pipelineId/index.route';
import { userPipelinesSearchSchema } from 'routes/_private/_route/users/$userId/pipelines/index.route';
import { newUserPipelineSearchSchema } from 'routes/_private/_route/users/$userId/pipelines/new.route';

export const Route = createFileRoute('/_private/_route/teams/$teamId')({
  beforeLoad: async ({ context, location, params }) => {
    const { queryClient, user } = context;
    let pTeam;
    try {
      pTeam = await queryClient.ensureQueryData(
        getTeamOptions({
          path: {
            team_slug: params.teamId,
          },
        }),
      );
    } catch (error) {
      // the API refuses an archived team (or one under an inactive company) to
      // its members; admins never hit this. Send them to their team list, where
      // the team is shown as archived.
      if (!isPersonAdmin(user)) {
        throw redirect({ replace: true, to: '/profile/teams' });
      }
      throw error;
    }
    const isExecutiveTeam = pTeam.company?.type === 'executive';

    // Executive teams are never browsed through the team UI — everyone
    // (including admins) is rewritten to the client user's `/users/...` routes,
    // which now host the full pipeline + add-recommendations experience.
    if (isExecutiveTeam) {
      const members = await queryClient.ensureQueryData(
        getTeamMembersOptions({
          path: {
            team_slug: params.teamId,
          },
          query: {
            team_uuids: [pTeam.uuid],
          },
        }),
      );

      const targetUserId =
        findExecutiveClientMemberId(members) ?? user.id ?? undefined;
      const pipelineId =
        'pipelineId' in params && typeof params.pipelineId === 'string'
          ? params.pipelineId
          : undefined;

      if (!targetUserId) {
        return {
          currentTeam: pTeam,
        };
      }

      if (location.pathname.endsWith('/pipelines/new')) {
        throw redirect({
          params: {
            userId: targetUserId,
          },
          replace: true,
          search: newUserPipelineSearchSchema.parse({}),
          to: '/users/$userId/pipelines/new',
        });
      }

      if (location.pathname.endsWith('/recommendations/new') && pipelineId) {
        throw redirect({
          params: {
            pipelineId,
            userId: targetUserId,
          },
          replace: true,
          search: newRecommendationsSearchSchema.parse({}),
          to: '/users/$userId/pipelines/$pipelineId/recommendations/new',
        });
      }

      if (location.pathname.endsWith('/edit') && pipelineId) {
        throw redirect({
          params: {
            pipelineId,
            userId: targetUserId,
          },
          replace: true,
          search: editUserPipelineSearchSchema.parse({}),
          to: '/users/$userId/pipelines/$pipelineId/edit',
        });
      }

      if (pipelineId) {
        throw redirect({
          params: {
            pipelineId,
            userId: targetUserId,
          },
          replace: true,
          search: userPipelineDetailsSearchSchema.parse({}),
          to: '/users/$userId/pipelines/$pipelineId',
        });
      }

      throw redirect({
        params: {
          userId: targetUserId,
        },
        replace: true,
        search: userPipelinesSearchSchema.parse({}),
        to: '/users/$userId/pipelines',
      });
    }

    return {
      currentTeam: pTeam,
    };
  },
  component: Outlet,
});
